import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { formatMoney, isPurchasePending } from 'features/company/companyUtils';
import { Card, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { formatDateTime, localizedText } from 'shared/utilities/format';

export function CompanyPurchaseDetailPage() {
  const { purchaseId } = useParams();
  const { companyId } = useCompany();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.companyPurchase(purchaseId),
    queryFn: () => companyApi.purchase(companyId, purchaseId),
  });

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const purchase = query.data?.purchase || query.data || {};
  const payment = purchase.payment || {};
  return (
    <div className="company-purchase-detail">
      <PageMeta titleKey="meta.companyPurchase" />
      <header className="page-header">
        <div>
          <h1>{localizedText(purchase, locale, ['plan_name', 'name'])}</h1>
          <StatusChip tone={purchase.status}>{purchase.status_label || t(`company.purchase.statuses.${purchase.status}`)}</StatusChip>
        </div>
        <Link className="btn btn-secondary" to={paths.companyPurchases}>{t('actions.back')}</Link>
      </header>
      <Card>
        <dl className="detail-list">
          <div><dt>{t('company.purchase.amount')}</dt><dd>{formatMoney(purchase.total_minor, purchase.currency, locale)}</dd></div>
          <div><dt>{t('company.purchase.credits')}</dt><dd>{purchase.attempt_quantity}</dd></div>
          <div><dt>{t('company.purchase.created')}</dt><dd>{formatDateTime(purchase.created_at, locale)}</dd></div>
          {purchase.paid_at ? <div><dt>{t('company.purchase.paidAt')}</dt><dd>{formatDateTime(purchase.paid_at, locale)}</dd></div> : null}
          {payment.reference || purchase.payment_reference ? (
            <div><dt>{t('company.purchase.reference')}</dt><dd>{payment.reference || purchase.payment_reference}</dd></div>
          ) : null}
        </dl>
        {isPurchasePending(purchase.status) ? (
          <div className="payment-instructions">
            <h2>{t('company.purchase.pendingTitle')}</h2>
            <p>{payment.instructions || t('company.purchase.pendingBody')}</p>
            <Link className="btn btn-primary" to={paths.companyPurchaseStatus(purchase.id)}>
              {t('company.purchase.checkStatus')}
            </Link>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
